import {
  TagsInput as MantineTagsInput,
  TagsInputProps as MantineTagsInputProps,
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import clsx from 'clsx';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ReadOnlyLock } from '../ReadOnlyLock';
import styles from './TagsInput.module.css';

export type TagsInputSize = 'md' | 'lg';

export interface TagsInputProps extends Omit<
  MantineTagsInputProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size' | 'readOnly'
> {
  /** Input size: 'md' (40px) or 'lg' (48px). Default: 'md' */
  size?: TagsInputSize;
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  isReadOnly?: boolean;
  /** Readonly visual style. 'label' (default) renders pills without trailing controls; 'lock' appends a lock icon to the right controls. Only applies when `isReadOnly` is true. */
  readOnlyVariant?: 'label' | 'lock';
}

export const TagsInput = forwardRef<HTMLInputElement, TagsInputProps>(
  (
    {
      size = 'md',
      label,
      error,
      disabled,
      isReadOnly,
      placeholder,
      isMasked = false,
      clearable = true,
      acceptValueOnBlur = true,
      readOnlyVariant = 'label',
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(
      defaultValue ?? []
    );
    const currentValue = isControlled ? value : uncontrolledValue;

    const handleChange = (val: string[]) => {
      if (!isControlled) {
        setUncontrolledValue(val);
      }
      onChange?.(val);
    };

    // --- Dynamic pill overflow: hide pills that don't fit, show "+N" badge ---
    const containerRef = useRef<HTMLDivElement>(null!);
    const [hiddenCount, setHiddenCount] = useState(0);

    const computeHiddenPills = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      const pillsList = container.querySelector(
        `.${styles.pillsList}`
      ) as HTMLElement | null;
      const inputEl = container.querySelector(
        `.${styles.input}`
      ) as HTMLElement | null;
      const sectionEl = container.querySelector(
        `.${styles.section}[data-position="right"]`
      ) as HTMLElement | null;

      if (!pillsList || !inputEl) {
        setHiddenCount(0);
        return;
      }

      // Step 1: Update input padding-right to match the actual right section width.
      if (sectionEl) {
        inputEl.style.paddingRight = `${sectionEl.offsetWidth}px`;
      }

      const pills = Array.from(
        pillsList.querySelectorAll(`.${styles.pill}`)
      ) as HTMLElement[];
      if (pills.length === 0) {
        setHiddenCount(0);
        return;
      }

      // Step 2: Reset all pills to visible so we can measure accurately.
      for (const pill of pills) {
        pill.style.display = '';
      }

      // Step 3: Measure and hide overflow pills
      const listRight = pillsList.getBoundingClientRect().right;
      let hidden = 0;
      let firstOverflow = false;

      for (const pill of pills) {
        if (firstOverflow) {
          pill.style.display = 'none';
          hidden++;
        } else if (pill.getBoundingClientRect().right > listRight + 1) {
          pill.style.display = 'none';
          hidden++;
          firstOverflow = true;
        }
      }

      setHiddenCount(hidden);
    }, []);

    useLayoutEffect(() => {
      computeHiddenPills();
    }, [currentValue, hiddenCount, computeHiddenPills]);

    useEffect(() => {
      const container = containerRef.current;

      if (container) {
        const observer = new ResizeObserver(computeHiddenPills);
        observer.observe(container);

        return () => {
          observer.disconnect();
        };
      }
      return undefined;
    }, [computeHiddenPills]);

    const showClearButton =
      clearable && currentValue.length > 0 && !disabled && !isReadOnly;

    const handleClear = (event: React.MouseEvent) => {
      event.stopPropagation();
      handleChange([]);
    };

    const isLockVariant = !!isReadOnly && readOnlyVariant === 'lock';

    // Right section: [+N badge] [clear X] [lock?]
    // No chevron — TagsInput has no dropdown. In the lock readonly variant a
    // lock icon is appended at the end.
    const hasRightContent = hiddenCount > 0 || showClearButton || isLockVariant;
    const rightSection = hasRightContent ? (
      <span className={styles.rightControls}>
        {hiddenCount > 0 && (
          <span className={styles.overflowBadge}>+{hiddenCount}</span>
        )}
        {showClearButton && (
          <button
            type="button"
            aria-label="Clear all values"
            className={styles.clearButton}
            onClick={handleClear}
            onMouseDown={(event) => event.preventDefault()}
            tabIndex={-1}
          >
            <IconX size={12} stroke={1.5} />
          </button>
        )}
        {isLockVariant && <ReadOnlyLock />}
      </span>
    ) : null;

    return (
      <div ref={containerRef}>
        <MantineTagsInput
          ref={ref}
          unstyled
          classNames={{
            root: styles.root,
            wrapper: styles.wrapper,
            input: clsx(styles.input, isMasked && 'fs-mask'),
            section: styles.section,
            label: styles.label,
            error: styles.error,
            required: styles.required,
            pill: styles.pill,
            pillsList: styles.pillsList,
            inputField: styles.inputField,
          }}
          wrapperProps={{
            'data-input-size': size,
            ...(isReadOnly && { 'data-readonly': true }),
            ...(isLockVariant && { 'data-readonly-variant': 'lock' }),
          }}
          rightSection={rightSection}
          rightSectionPointerEvents="auto"
          value={currentValue}
          onChange={handleChange}
          clearable={false}
          acceptValueOnBlur={acceptValueOnBlur}
          label={label}
          error={error}
          disabled={disabled}
          readOnly={isReadOnly}
          placeholder={isReadOnly && !isLockVariant ? '-' : placeholder}
          {...props}
        />
      </div>
    );
  }
);

TagsInput.displayName = 'TagsInput';
